import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { db } from "../firebaseConfig";
import { doc, onSnapshot, setDoc } from "firebase/firestore";

const ThemeContext = createContext();

const LS = {
  theme: "app-theme",
  tz: "app-timezone",
  night: "app-auto-nightStart",
  day: "app-auto-dayStart",
  accent: "app-accent",
};

const ACCENTS = {
  blue: "#2563EB",
  indigo: "#4F46E5",
  green: "#16A34A",
  purple: "#9333EA",
  pink: "#DB2777",
  red: "#DC2626",
  orange: "#F97316",
  yellow: "#EAB308",
  cyan: "#06B6D4",
  emerald: "#059669",
};

function parseGMTOffsetToMinutes(tz) {
  const raw = String(tz).trim().toUpperCase();
  const base = raw.match(/^(GMT|UTC)[+-]\d{1,2}(?::\d{2})?/i)?.[0];
  if (!base) return null;

  const m = base.match(/^(GMT|UTC)([+-]\d{1,2})(?::(\d{2}))?$/i);
  if (!m) return null;

  const sign = m[2].startsWith("-") ? -1 : 1;
  const hours = Math.abs(parseInt(m[2], 10));
  const mins = m[3] ? parseInt(m[3], 10) : 0;
  return sign * (hours * 60 + mins);
}

function getHourInTimeZone(timeZone) {
  try {
    const offMin = parseGMTOffsetToMinutes(timeZone);
    if (offMin !== null) {
      const now = new Date();
      const utcMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();
      const localMinutes = (utcMinutes + offMin + 24 * 60) % (24 * 60);
      return Math.floor(localMinutes / 60);
    }

    const hour = new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      hour12: false,
      timeZone,
    }).format(new Date());

    return Number(hour);
  } catch {
    return new Date().getHours();
  }
}

function computeAutoTheme({ timeZone, nightStart, dayStart }) {
  const h = getHourInTimeZone(timeZone);
  return h >= nightStart || h < dayStart ? "dark" : "light";
}

function hexToRgb(hex) {
  const h = hex.replace("#", "").trim();
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  const int = parseInt(full, 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}
function rgbToHex({ r, g, b }) {
  const to = (x) => x.toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}
function mix(hexA, hexB, t) {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  const r = Math.round(a.r + (b.r - a.r) * t);
  const g = Math.round(a.g + (b.g - a.g) * t);
  const b2 = Math.round(a.b + (b.b - a.b) * t);
  return rgbToHex({ r, g, b: b2 });
}

export function ThemeProvider({ user, children }) {
  const [theme, setThemeState] = useState(
    () => localStorage.getItem(LS.theme) || "auto",
  );
  const [timeZone, setTimeZoneState] = useState(
    () => localStorage.getItem(LS.tz) || "GMT-6",
  );
  const [nightStart, setNightStartState] = useState(() =>
    Number(localStorage.getItem(LS.night) || 19),
  );
  const [dayStart, setDayStartState] = useState(() =>
    Number(localStorage.getItem(LS.day) || 7),
  );

  // ✅ Accent token
  const [accent, setAccentState] = useState(
    () => localStorage.getItem(LS.accent) || "blue",
  );

  const mergeUiPrefs = useCallback(
    async (patch) => {
      if (!user?.uid) return;
      await setDoc(doc(db, "users", user.uid, "prefs", "ui"), patch, {
        merge: true,
      });
    },
    [user?.uid],
  );

  const setTheme = useCallback(
    async (newTheme) => {
      setThemeState(newTheme);
      localStorage.setItem(LS.theme, newTheme);
      await mergeUiPrefs({ tema: newTheme });
    },
    [mergeUiPrefs],
  );

  const setAppTimeZone = useCallback(
    async (tz, { persist = true } = {}) => {
      setTimeZoneState(tz);
      localStorage.setItem(LS.tz, tz);
      if (persist) await mergeUiPrefs({ zonaHoraria: tz });
    },
    [mergeUiPrefs],
  );

  const setAutoHours = useCallback(
    async ({ night, day }, { persist = true } = {}) => {
      let nextNight = nightStart;
      let nextDay = dayStart;

      if (typeof night === "number") {
        nextNight = night;
        setNightStartState(night);
        localStorage.setItem(LS.night, String(night));
      }
      if (typeof day === "number") {
        nextDay = day;
        setDayStartState(day);
        localStorage.setItem(LS.day, String(day));
      }

      if (persist) {
        await mergeUiPrefs({
          autoTheme: { nightStart: nextNight, dayStart: nextDay },
        });
      }
    },
    [mergeUiPrefs, nightStart, dayStart],
  );

  const setAccent = useCallback(
    async (token, { persist = true } = {}) => {
      const next = ACCENTS[token] ? token : "blue";
      setAccentState(next);
      localStorage.setItem(LS.accent, next);
      if (persist) await mergeUiPrefs({ colorAcento: next });
    },
    [mergeUiPrefs],
  );

  const resolvedTheme = useMemo(() => {
    if (theme !== "auto") return theme;
    return computeAutoTheme({ timeZone, nightStart, dayStart });
  }, [theme, timeZone, nightStart, dayStart]);

  // ✅ Aplicar theme + accent en :root (una sola vez)
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", resolvedTheme);

    const base = ACCENTS[accent] || ACCENTS.blue;
    const hover = mix(base, "#000000", 0.08); // un poco más oscuro
    const soft = mix(base, "#ffffff", 0.78); // suave para backgrounds
    const ring = mix(base, "#ffffff", 0.55); // ring

    document.documentElement.style.setProperty("--accent", base);
    document.documentElement.style.setProperty("--accent-hover", hover);
    document.documentElement.style.setProperty("--accent-soft", soft);
    document.documentElement.style.setProperty("--accent-ring", ring);
  }, [resolvedTheme, accent]);

  // auto tick
  useEffect(() => {
    if (theme !== "auto") return;

    const tick = () => {
      const next = computeAutoTheme({ timeZone, nightStart, dayStart });
      document.documentElement.setAttribute("data-theme", next);
    };

    tick();
    const id = setInterval(tick, 60_000);
    const onVis = () => !document.hidden && tick();

    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [theme, timeZone, nightStart, dayStart]);

  // Firestore sync
  useEffect(() => {
    if (!user?.uid) return;

    // ✅ Nuevo path por usuario
    const ref = doc(db, "users", user.uid, "prefs", "ui");

    const unsub = onSnapshot(ref, (snap) => {
      const pref = snap.exists() ? snap.data() || {} : {};

      // ✅ nuevos campos (y fallback por si traes legacy)
      const dbTheme = pref.theme ?? pref.tema;
      const dbTZ = pref.timeZone ?? pref.zonaHoraria;
      const dbNight = pref.autoTheme?.nightStart;
      const dbDay = pref.autoTheme?.dayStart;

      const dbAccent = pref.accent ?? pref.colorAcento ?? pref.accentColor;

      if (dbTheme) {
        setThemeState((prev) => {
          if (dbTheme !== prev) {
            localStorage.setItem(LS.theme, dbTheme);
            return dbTheme;
          }
          return prev;
        });
      }

      if (dbTZ) {
        setTimeZoneState((prev) => {
          if (dbTZ !== prev) {
            localStorage.setItem(LS.tz, dbTZ);
            return dbTZ;
          }
          return prev;
        });
      }

      if (typeof dbNight === "number") {
        setNightStartState((prev) => {
          if (dbNight !== prev) {
            localStorage.setItem(LS.night, String(dbNight));
            return dbNight;
          }
          return prev;
        });
      }

      if (typeof dbDay === "number") {
        setDayStartState((prev) => {
          if (dbDay !== prev) {
            localStorage.setItem(LS.day, String(dbDay));
            return dbDay;
          }
          return prev;
        });
      }

      if (dbAccent && ACCENTS[dbAccent]) {
        setAccentState((prev) => {
          if (dbAccent !== prev) {
            localStorage.setItem(LS.accent, dbAccent);
            return dbAccent;
          }
          return prev;
        });
      }
    });

    return () => unsub();
  }, [user?.uid]);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      resolvedTheme,
      timeZone,
      setAppTimeZone,
      autoTheme: { nightStart, dayStart },
      setAutoHours,

      // ✅ NUEVO
      accent,
      setAccent,
      accentsMap: ACCENTS,
    }),
    [
      theme,
      setTheme,
      resolvedTheme,
      timeZone,
      setAppTimeZone,
      nightStart,
      dayStart,
      setAutoHours,
      accent,
      setAccent,
    ],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
